import React, { useState } from 'react';
import type { PlanT, MetaT } from '@mentimeter/plans-content';
import type { HandlePlanClickT } from '@mentimeter/checkout-modal';
import { getFormattedPrice } from '@mentimeter/billing';
import { ContactSalesModal } from '@mentimeter/workspace';
import { Box } from '@mentimeter/ragnar-ui/box';
import { RecommendedHeader } from '@mentimeter/plans-ui';
import { useFlag } from '@mentimeter/flags/confidence/client';
import { CheckoutModalsWrapper } from '../../CheckoutModalsWrapper';
import {
  PlanCell,
  ActionButton,
  Heading,
  AdditionalPriceInfo,
  DiscountedPrice,
  MainPrice,
  UniqueSellingPoints,
} from './components';
import { UniqueSellingPointsValueOriented } from './components/UniqueSellingPointsValueOriented';

const PLAN_SECTIONS_COUNT = 7;

export function PlansPreviewGrid({ plans }: { plans: PlanT[] }) {
  const [showSalesModal, setShowSalesModal] = useState(false);

  const sectionsCount = PLAN_SECTIONS_COUNT;

  const numberOfPlans = plans.length;

  const { value: showValueOrientedUsps } = useFlag(
    'pug-value-oriented-pricing-page.enabled',
    false,
  );

  return (
    <CheckoutModalsWrapper>
      {({ handlePlanClick }) => (
        <Box
          display="grid"
          gridAutoFlow={['row', null, 'column']}
          gridTemplateColumns={[
            null,
            null,
            `repeat(${numberOfPlans}, minmax(0,320px))`,
          ]}
          gridTemplateRows={`repeat(${sectionsCount}, max-content)`}
          columnGap={3}
        >
          <ContactSalesModal
            showSalesModal={showSalesModal}
            setShowSalesModal={setShowSalesModal}
            trackingContext="Plans page"
          />
          {plans.map((plan) => {
            const sections = getPlanSections(plan, handlePlanClick);

            return <React.Fragment key={plan.id}>{sections}</React.Fragment>;
          })}
        </Box>
      )}
    </CheckoutModalsWrapper>
  );

  function getPlanSections(plan: PlanT, handlePlanClick: HandlePlanClickT) {
    const discountPrice = calculateDiscountedPlanPrice(plan);
    const shouldShowYearlyDiscount = (item: MetaT) => {
      return Boolean(
        !plan.discount &&
          item.yearlyDiscount &&
          item.yearlyDiscountText &&
          !plan.isPriceLoading,
      );
    };

    const highlight = Boolean(plan.highlight);

    return [
      ...[
        highlight ? (
          <RecommendedHeader key={plan.id + '-top-header'} planId={plan.id} />
        ) : (
          <Box key={plan.id + '-top-header'} />
        ),
      ],
      <PlanCell
        key={plan.id + '-heading'}
        highlight={highlight}
        showTopBorder={!highlight}
        topPadding="space8"
      >
        <Heading heading={plan.heading} subheading={plan.subheading} />
      </PlanCell>,
      <PlanCell key={`${plan.id}-price`} highlight={highlight} pb="space1.5">
        {!!plan.discount && !plan.isPriceLoading && (
          <DiscountedPrice plan={plan} discountPrice={discountPrice} />
        )}
        <MainPrice plan={plan} />
      </PlanCell>,
      <PlanCell key={`${plan.id}-price-info`} highlight={highlight} pt="space0">
        <AdditionalPriceInfo
          plan={plan}
          showYearlyDiscount={shouldShowYearlyDiscount}
        />
      </PlanCell>,

      <PlanCell key={`${plan.id}-empty-cell`} p="space0" m="space0" />,
      <PlanCell key={plan.id + '-button'} highlight={highlight}>
        <ActionButton plan={plan} onPlanClick={handlePlanClick} />
      </PlanCell>,
      <PlanCell
        key={plan.id + '-usps'}
        highlight={highlight}
        showBottomBorder
        bottomPadding="space8"
      >
        {showValueOrientedUsps ? (
          <UniqueSellingPointsValueOriented
            uspHeading={plan.uspHeading}
            usps={plan.usp}
          />
        ) : (
          <UniqueSellingPoints uspHeading={plan.uspHeading} usps={plan.usp} />
        )}
      </PlanCell>,
    ];
  }
}

function calculateDiscountedPlanPrice(plan: PlanT): string {
  return (
    (plan?.currencySymbol &&
      plan?.priceFormat &&
      plan.discount?.baseAmount &&
      getFormattedPrice(
        plan.discount.baseAmount,
        plan.currencySymbol,
        plan.priceFormat,
      )) ||
    ''
  );
}
